export const MILES_TO_METERS = 1609.344
export const EYE_HEIGHT_M = 1.75
export const VIEWSHED_ZOOM = 15

// lng/lat → tile index at zoom z
export function lngLatToTile(lng, lat, z) {
  const n = Math.pow(2, z)
  const tx = Math.floor((lng + 180) / 360 * n)
  const latRad = lat * Math.PI / 180
  const ty = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n)
  return { tx, ty }
}

// lng/lat → tile index + pixel offset within that tile
export function lngLatToTilePx(lng, lat, z) {
  const n = Math.pow(2, z)
  const fracX = (lng + 180) / 360 * n
  const latRad = lat * Math.PI / 180
  const fracY = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n
  const tx = Math.floor(fracX)
  const ty = Math.floor(fracY)
  const px = Math.floor((fracX - tx) * 256)
  const py = Math.floor((fracY - ty) * 256)
  return { tx, ty, px, py }
}

// Meters per pixel at zoom z and latitude (degrees)
export function metersPerPixelAt(latDeg, z) {
  const latRad = latDeg * Math.PI / 180
  return (2 * Math.PI * 6371000 * Math.cos(latRad)) / (Math.pow(2, z) * 256)
}

// Geographic bounding box of a tile
export function tileBbox(tx, ty, z) {
  const n = Math.pow(2, z)
  const west  = tx / n * 360 - 180
  const east  = (tx + 1) / n * 360 - 180
  const north = Math.atan(Math.sinh(Math.PI * (1 - 2 * ty / n))) * 180 / Math.PI
  const south = Math.atan(Math.sinh(Math.PI * (1 - 2 * (ty + 1) / n))) * 180 / Math.PI
  return { west, east, north, south }
}

// Geographic bounds of a stitched grid: NW corner of its first tile to SE corner of its last.
// This, not the viewshedBbox the tiles were chosen from, is what the grid's pixels cover.
export function gridBounds(minTx, minTy, width, height, z) {
  const maxTx = minTx + width / 256 - 1
  const maxTy = minTy + height / 256 - 1
  const { west, north } = tileBbox(minTx, minTy, z)
  const { east, south } = tileBbox(maxTx, maxTy, z)
  return { west, east, north, south }
}

// All tiles covering a geographic bounding box at zoom z
export function tilesInBbox(west, south, east, north, z) {
  const { tx: txMin, ty: tyMax } = lngLatToTile(west, south, z)
  const { tx: txMax, ty: tyMin } = lngLatToTile(east, north, z)
  const tiles = []
  for (let ty = tyMin; ty <= tyMax; ty++) {
    for (let tx = txMin; tx <= txMax; tx++) {
      tiles.push({ tx, ty })
    }
  }
  return tiles
}

// Square bounding box centered on observer, expanded by radiusM in all directions
export function viewshedBbox(lng, lat, radiusM) {
  const latPerM = 1 / 111320
  const lngPerM = 1 / (111320 * Math.cos(lat * Math.PI / 180))
  return {
    west:  lng - radiusM * lngPerM,
    east:  lng + radiusM * lngPerM,
    south: lat - radiusM * latPerM,
    north: lat + radiusM * latPerM,
  }
}

// Stitch elevation tiles from the cache into one flat Float32Array grid.
// Returns grid, pixel dimensions, and origin tile coords for offset math.
export function stitchElevGrid(elevCache, tiles) {
  const txs = tiles.map(t => t.tx)
  const tys = tiles.map(t => t.ty)
  const minTx = Math.min(...txs), maxTx = Math.max(...txs)
  const minTy = Math.min(...tys), maxTy = Math.max(...tys)
  const W = (maxTx - minTx + 1) * 256
  const H = (maxTy - minTy + 1) * 256
  const grid = new Float32Array(W * H)

  for (const { tx, ty } of tiles) {
    const elev = elevCache.get(`${VIEWSHED_ZOOM}/${tx}/${ty}`)
    if (!elev) continue
    const offX = (tx - minTx) * 256
    const offY = (ty - minTy) * 256
    for (let py = 0; py < 256; py++) {
      grid.set(elev.subarray(py * 256, py * 256 + 256), (offY + py) * W + offX)
    }
  }

  return { grid, width: W, height: H, minTx, minTy }
}
